import React, { useState, useEffect } from 'react';
import { Session, Feature, Component, PlatformItem, Platform } from '../../types';
import { AppMessage } from '../../utils/messages';

interface AddComponentModalProps {
  session?: Session; // Optional for CreateSession
  allFeatures: Feature[];
  allComponents: Component[];
  allPlatforms: PlatformItem[];
  isOpen: boolean;
  onClose: () => void;
  onComponentsAdded: (featureIds: string[], componentIds: string[]) => Promise<void> | void;
  showMessage: (msg: AppMessage) => void;
  alreadySelectedComponentIds?: string[]; // Components already selected in form (e.g., from test plan)
  milestone?: string; // Selected milestone to filter components
  milestones?: Record<string, { startDate: string; endDate?: string }>; // Milestone metadata
}

const AddComponentModal: React.FC<AddComponentModalProps> = ({
  session,
  allFeatures,
  allComponents,
  allPlatforms,
  isOpen,
  onClose,
  onComponentsAdded,
  showMessage,
  alreadySelectedComponentIds = [],
  milestone,
  milestones = {}
}) => {
  // Helper function to filter components by milestone
  const filterComponentsByMilestone = (componentsToFilter: Component[], selectedMilestone: string | undefined): Component[] => {
    if (!selectedMilestone || !selectedMilestone.trim()) {
      return componentsToFilter;
    }
    
    const milestoneMeta = milestones[selectedMilestone];
    if (!milestoneMeta) {
      // If no metadata, filter by exact milestone match
      return componentsToFilter.filter(c => c.milestone === selectedMilestone);
    }
    
    const milestoneStartDate = new Date(milestoneMeta.startDate);
    
    return componentsToFilter.filter(c => {
      // Include components from the selected milestone
      if (c.milestone === selectedMilestone) {
        return true;
      }
      
      // Include components from milestones with start dates <= selected milestone start date
      const componentMilestoneMeta = milestones[c.milestone];
      if (!componentMilestoneMeta) {
        // If component milestone has no metadata, only include if exact match
        return false;
      }
      
      const componentMilestoneStart = new Date(componentMilestoneMeta.startDate);
      return componentMilestoneStart <= milestoneStartDate;
    });
  };
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<string>>(new Set());
  const [selectedComponentIds, setSelectedComponentIds] = useState<Set<string>>(new Set());
  const [selectedPlatformIds, setSelectedPlatformIds] = useState<Set<string>>(new Set());
  const [featureSearchTerm, setFeatureSearchTerm] = useState<string>('');
  const [componentSearchTerm, setComponentSearchTerm] = useState<string>('');

  // Get components already in session (if session exists) OR already selected in form (e.g., from test plan)
  // Combine both sources to show all components that are already added
  const sessionComponentIds = new Set([
    ...(session?.components || []),
    ...alreadySelectedComponentIds
  ]);
  
  // Show all components (not just available ones) so users can see what's already added
  // Filter components by milestone first, then by selected features and platforms
  let filteredComponents = filterComponentsByMilestone(allComponents, milestone);
  
  // Calculate how many components are hidden due to milestone filtering
  const hiddenByMilestoneCount = milestone ? allComponents.length - filterComponentsByMilestone(allComponents, milestone).length : 0;
  
  // Filter by features
  if (selectedFeatureIds.size > 0) {
    filteredComponents = filteredComponents.filter(c => selectedFeatureIds.has(c.featureId));
  }
  
  // Filter by platforms (if any platforms are selected)
  if (selectedPlatformIds.size > 0) {
    const selectedPlatforms = allPlatforms
      .filter(p => selectedPlatformIds.has(p.id))
      .map(p => p.name as Platform);
    
    filteredComponents = filteredComponents.filter(c => {
      // If component has no platforms assigned, include it (don't filter it out)
      if (!c.platforms || c.platforms.length === 0) {
        return true;
      }
      // Include component if it has at least one of the selected platforms
      return c.platforms.some(platform => selectedPlatforms.includes(platform));
    });
  }

  // Get unique features that have components (including already added ones)
  const featuresWithComponents = allFeatures.filter(feature => 
    allComponents.some(c => c.featureId === feature.id)
  );

  // Filter features by search term
  const filteredFeatures = featuresWithComponents.filter(feature =>
    feature.name.toLowerCase().includes(featureSearchTerm.toLowerCase())
  );

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedFeatureIds(new Set());
      setSelectedComponentIds(new Set());
      setSelectedPlatformIds(new Set());
      setFeatureSearchTerm('');
      setComponentSearchTerm('');
    }
  }, [isOpen]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Prevent scrolling on body
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore original overflow when modal closes
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleToggleFeature = (featureId: string) => {
    setSelectedFeatureIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(featureId)) {
        newSet.delete(featureId);
        // Deselect all components from this feature when feature is deselected
        const componentsFromFeature = allComponents
          .filter(c => c.featureId === featureId)
          .map(c => c.id);
        setSelectedComponentIds(prevComponents => {
          const newComponentSet = new Set(prevComponents);
          componentsFromFeature.forEach(id => newComponentSet.delete(id));
          return newComponentSet;
        });
      } else {
        newSet.add(featureId);
      }
      return newSet;
    });
  };

  const handleToggleComponent = (componentId: string) => {
    setSelectedComponentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(componentId)) {
        newSet.delete(componentId);
      } else {
        newSet.add(componentId);
      }
      return newSet;
    });
  };

  const handleTogglePlatform = (platformId: string) => {
    setSelectedPlatformIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(platformId)) {
        newSet.delete(platformId);
      } else {
        newSet.add(platformId);
      }
      return newSet;
    });
  };

  const handleSelectAllFeatures = () => {
    // Use filtered features for select all
    const filteredFeatureIds = new Set(filteredFeatures.map(f => f.id));
    const allFilteredSelected = filteredFeatureIds.size > 0 && Array.from(filteredFeatureIds).every(id => selectedFeatureIds.has(id));
    
    if (allFilteredSelected) {
      // Deselect all filtered features and their components
      setSelectedFeatureIds(prev => {
        const newSet = new Set(prev);
        filteredFeatureIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      // Deselect components from filtered features
      const componentsFromFilteredFeatures = allComponents
        .filter(c => filteredFeatureIds.has(c.featureId))
        .map(c => c.id);
      setSelectedComponentIds(prev => {
        const newSet = new Set(prev);
        componentsFromFilteredFeatures.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all filtered features
      setSelectedFeatureIds(prev => {
        const newSet = new Set(prev);
        filteredFeatureIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  const handleSelectAllComponents = () => {
    // Only select available components (not already in session)
    const availableFilteredComponents = filteredComponents.filter(c => !sessionComponentIds.has(c.id));
    const availableIds = new Set(availableFilteredComponents.map(c => c.id));
    
    // Check if all available components are selected
    const allSelected = availableIds.size > 0 && Array.from(availableIds).every(id => selectedComponentIds.has(id));
    
    if (allSelected) {
      // Deselect all available components
      setSelectedComponentIds(prev => {
        const newSet = new Set(prev);
        availableIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all available components
      setSelectedComponentIds(prev => {
        const newSet = new Set(prev);
        availableIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  const handleAddComponents = async () => {
    if (selectedFeatureIds.size === 0) {
      showMessage({ type: 'error', text: 'Please select at least one feature.' });
      return;
    }

    if (selectedComponentIds.size === 0) {
      showMessage({ type: 'error', text: 'Please select at least one component.' });
      return;
    }

    // Filter out components that are already in the session
    const newComponentIds = Array.from(selectedComponentIds).filter(id => !sessionComponentIds.has(id));
    
    if (newComponentIds.length === 0) {
      showMessage({ type: 'error', text: 'All selected components are already in the session.' });
      return;
    }

    const featureIdsArray = Array.from(selectedFeatureIds);
    const componentIdsArray = newComponentIds;

    // Reset form and close modal
    setSelectedFeatureIds(new Set());
    setSelectedComponentIds(new Set());
    onClose();

    // Call the callback to handle adding the features and components
    const result = onComponentsAdded(featureIdsArray, componentIdsArray);
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        console.error('Error adding features and components:', error);
        if (error instanceof Error) {
          showMessage({ type: 'error', text: error.message || 'Failed to add features and components. Please try again.' });
        } else {
          showMessage({ type: 'error', text: 'Failed to add features and components. Please try again.' });
        }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        overflow: 'hidden',
        zIndex: 1000,
        pointerEvents: 'auto'
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.preventDefault()}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="card modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '1200px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <h2 className="title-lg mb-md">{session ? 'Add Features & Components to Session' : 'Select Features & Components'}</h2>
        
        {/* Two Column Layout */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '2rem',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden'
        }}>

        {/* Left Column - Features & Platform Filter */}
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Feature multi-select */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <label className="label">
                Select Features <span className="text-danger">*</span>
              </label>
            {filteredFeatures.length > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSelectAllFeatures}
                style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
              >
                {selectedFeatureIds.size === filteredFeatures.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          {/* Feature Search */}
          {featuresWithComponents.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <input
                type="text"
                placeholder="🔍 Search features..."
                value={featureSearchTerm}
                onChange={(e) => setFeatureSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid var(--primary)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--void)',
                  color: 'var(--text-primary)',
                  fontSize: '0.9375rem',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 0 0 2px rgba(0, 212, 255, 0.1)',
                  fontWeight: '500'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--cyan-bright)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 212, 255, 0.2), 0 2px 8px rgba(0, 212, 255, 0.15)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--primary)';
                  e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 212, 255, 0.1)';
                }}
              />
            </div>
          )}
          
          {featuresWithComponents.length === 0 ? (
            <div className="text-center" style={{ padding: '1.5rem', backgroundColor: 'var(--surface-elevated)', borderRadius: '4px' }}>
              <p className="text-muted" style={{ margin: 0 }}>
                No features available.
              </p>
            </div>
          ) : filteredFeatures.length === 0 ? (
            <div className="text-center" style={{ padding: '1.5rem', backgroundColor: 'var(--surface-elevated)', borderRadius: '4px' }}>
              <p className="text-muted" style={{ margin: 0 }}>
                No features match "{featureSearchTerm}".
              </p>
            </div>
          ) : (
            <>
              <div style={{ 
                flex: 1,
                overflowY: 'auto', 
                border: '1px solid var(--border-color)', 
                borderRadius: '4px',
                padding: '0.5rem',
                minHeight: 0
              }}>
                {filteredFeatures.map(feature => {
                  const isSelected = selectedFeatureIds.has(feature.id);
                  const featureComponents = allComponents.filter(c => c.featureId === feature.id);
                  
                  return (
                    <div
                      key={feature.id}
                      onClick={() => handleToggleFeature(feature.id)}
                      className="selectable-item"
                      style={{
                        padding: '0.875rem',
                        marginBottom: '0.5rem',
                        backgroundColor: isSelected ? 'var(--primary-transparent)' : 'var(--surface-color)',
                        color: isSelected ? 'var(--primary)' : 'var(--text-color)',
                        border: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border-color)'}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'var(--surface-elevated)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'var(--surface-color)';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleFeature(feature.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: 'pointer', width: '18px', height: '18px', accentColor: 'var(--primary)' }}
                      />
                      <div style={{ flex: 1, color: 'inherit' }}>
                        <div style={{ fontWeight: '500', fontSize: '0.9375rem', color: 'inherit' }}>{feature.name}</div>
                        <div style={{ fontSize: '0.8125rem', opacity: isSelected ? 0.9 : 0.7, marginTop: '0.25rem', color: 'inherit' }}>
                          {(() => {
                            const availableCount = featureComponents.filter(c => !sessionComponentIds.has(c.id)).length;
                            const totalCount = featureComponents.length;
                            if (availableCount === totalCount) {
                              return `${totalCount} component${totalCount !== 1 ? 's' : ''} available`;
                            } else {
                              return `${availableCount} of ${totalCount} component${totalCount !== 1 ? 's' : ''} available`;
                            }
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {selectedFeatureIds.size > 0 && (
                <div className="text-muted" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  {selectedFeatureIds.size} feature{selectedFeatureIds.size !== 1 ? 's' : ''} selected
                </div>
              )}
            </>
          )}
            </div>
          </div>

          {/* Right Column - Components */}
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Platform filter - compact */}
            {allPlatforms.length > 0 && (
              <div style={{ 
                marginBottom: '0.75rem', 
                padding: '0.5rem 0.75rem',
                background: 'var(--surface-elevated)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Platform:
                </span>
                {allPlatforms.map(platform => {
                  const isSelected = selectedPlatformIds.has(platform.id);
                  return (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() => handleTogglePlatform(platform.id)}
                      style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: isSelected ? '1px solid var(--cyan-glow)' : '1px solid var(--border-color)',
                        background: isSelected ? 'var(--cyan-transparent)' : 'transparent',
                        color: isSelected ? 'var(--cyan-glow)' : 'var(--text-muted)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {platform.name}
                    </button>
                  );
                })}
                {selectedPlatformIds.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedPlatformIds(new Set())}
                    style={{ 
                      fontSize: '0.7rem', 
                      padding: '0.2rem 0.4rem',
                      borderRadius: '4px',
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Component multi-select */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <label className="label">
              Select Components <span className="text-danger">*</span>
            </label>
            {filteredComponents.length > 0 && selectedFeatureIds.size > 0 && (() => {
              const availableFilteredComponents = filteredComponents.filter(c => !sessionComponentIds.has(c.id));
              const availableIds = new Set(availableFilteredComponents.map(c => c.id));
              const allAvailableSelected = availableIds.size > 0 && Array.from(availableIds).every(id => selectedComponentIds.has(id));
              
              return (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleSelectAllComponents}
                  style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                >
                  {allAvailableSelected ? 'Deselect All' : 'Select All'}
                </button>
              );
            })()}
          </div>

          {/* Component Search */}
          {selectedFeatureIds.size > 0 && filteredComponents.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <input
                type="text"
                placeholder="🔍 Search components..."
                value={componentSearchTerm}
                onChange={(e) => setComponentSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid var(--primary)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--void)',
                  color: 'var(--text-primary)',
                  fontSize: '0.9375rem',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 0 0 2px rgba(0, 212, 255, 0.1)',
                  fontWeight: '500'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--cyan-bright)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 212, 255, 0.2), 0 2px 8px rgba(0, 212, 255, 0.15)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--primary)';
                  e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 212, 255, 0.1)';
                }}
              />
            </div>
          )}
          
          {/* Info about hidden components due to milestone */}
          {milestone && hiddenByMilestoneCount > 0 && (
            <div style={{ 
              padding: '0.5rem 0.75rem', 
              backgroundColor: 'rgba(234, 179, 8, 0.1)', 
              border: '1px solid rgba(234, 179, 8, 0.3)',
              borderRadius: '4px',
              marginBottom: '0.75rem',
              fontSize: '0.8rem',
              color: 'var(--text-muted)'
            }}>
              <span style={{ color: 'rgb(234, 179, 8)', marginRight: '0.5rem' }}>ℹ</span>
              {hiddenByMilestoneCount} component{hiddenByMilestoneCount !== 1 ? 's' : ''} scheduled for future milestones {hiddenByMilestoneCount !== 1 ? 'are' : 'is'} not shown.
            </div>
          )}

          {selectedFeatureIds.size === 0 ? (
            <div className="text-center" style={{ padding: '1.5rem', backgroundColor: 'var(--surface-elevated)', borderRadius: '4px' }}>
              <p className="text-muted" style={{ margin: 0 }}>
                Please select at least one feature to see components.
              </p>
            </div>
          ) : filteredComponents.length === 0 ? (
            <div className="text-center" style={{ padding: '1.5rem', backgroundColor: 'var(--surface-elevated)', borderRadius: '4px' }}>
              <p className="text-muted" style={{ margin: 0 }}>
                No components for selected features.
              </p>
            </div>
          ) : (
            <>
              <div style={{ 
                flex: 1,
                overflowY: 'auto', 
                border: '1px solid var(--border-color)', 
                borderRadius: '4px',
                padding: '0.5rem',
                minHeight: 0
              }}>
                {Array.from(selectedFeatureIds).map(featureId => {
                  const feature = allFeatures.find(f => f.id === featureId);
                  let featureComponents = filteredComponents.filter(c => c.featureId === featureId);
                  
                  // Filter components by search term
                  if (componentSearchTerm) {
                    featureComponents = featureComponents.filter(c =>
                      c.name.toLowerCase().includes(componentSearchTerm.toLowerCase())
                    );
                  }
                  
                  if (featureComponents.length === 0) {
                    // Show feature header even if no components match search
                    if (componentSearchTerm) {
                      return (
                        <div key={featureId} style={{ marginBottom: '1rem' }}>
                          <div style={{ 
                            padding: '0.5rem 0.75rem', 
                            backgroundColor: 'var(--surface-elevated)', 
                            borderRadius: '4px',
                            marginBottom: '0.5rem',
                            border: '1px solid var(--border-color)'
                          }}>
                            <div style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-color)' }}>
                              {feature?.name || 'Unknown Feature'}
                            </div>
                          </div>
                          <div style={{ paddingLeft: '0.5rem', padding: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                            No components match "{componentSearchTerm}".
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }
                  
                  return (
                    <div key={featureId} style={{ marginBottom: '1rem' }}>
                      {/* Feature header */}
                      <div style={{ 
                        padding: '0.5rem 0.75rem', 
                        backgroundColor: 'var(--surface-elevated)', 
                        borderRadius: '4px',
                        marginBottom: '0.5rem',
                        border: '1px solid var(--border-color)'
                      }}>
                        <div style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-color)' }}>
                          {feature?.name || 'Unknown Feature'}
                        </div>
                      </div>
                      
                      {/* Components for this feature */}
                      <div style={{ paddingLeft: '0.5rem' }}>
                        {featureComponents.map(component => {
                          const isSelected = selectedComponentIds.has(component.id);
                          const isAlreadyInSession = sessionComponentIds.has(component.id);
                          const isDisabled = isAlreadyInSession;
                          
                          return (
                            <div
                              key={component.id}
                              onClick={() => !isDisabled && handleToggleComponent(component.id)}
                              className="selectable-item"
                              style={{
                                padding: '0.875rem',
                                marginBottom: '0.5rem',
                                backgroundColor: isDisabled 
                                  ? 'var(--surface-elevated)' 
                                  : isSelected 
                                    ? 'var(--primary-transparent)' 
                                    : 'var(--surface-color)',
                                color: isDisabled 
                                  ? 'var(--text-secondary)' 
                                  : isSelected 
                                    ? 'var(--primary)' 
                                    : 'var(--text-color)',
                                border: `1px solid ${isDisabled 
                                  ? 'var(--border-color)' 
                                  : isSelected 
                                    ? 'var(--primary)' 
                                    : 'var(--border-color)'}`,
                                borderRadius: '4px',
                                cursor: isDisabled ? 'default' : 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                opacity: isDisabled ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected && !isDisabled) {
                                  e.currentTarget.style.backgroundColor = 'var(--surface-elevated)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected && !isDisabled) {
                                  e.currentTarget.style.backgroundColor = 'var(--surface-color)';
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => !isDisabled && handleToggleComponent(component.id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: isDisabled ? 'default' : 'pointer', width: '18px', height: '18px', accentColor: 'var(--primary)' }}
                              />
                              <div style={{ flex: 1, color: 'inherit' }}>
                                <div style={{ fontWeight: '500', fontSize: '0.9375rem', color: 'inherit' }}>
                                  {component.name}
                                  {isDisabled && (
                                    <span style={{ marginLeft: '0.5rem', fontSize: '0.8125rem', fontStyle: 'italic' }}>
                                      (already in session)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {selectedComponentIds.size > 0 && (
                <div className="text-muted" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  {selectedComponentIds.size} component{selectedComponentIds.size !== 1 ? 's' : ''} selected
                </div>
              )}
            </>
          )}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-sm" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddComponents}
            type="button"
            disabled={selectedComponentIds.size === 0 || selectedFeatureIds.size === 0}
          >
            Add {selectedComponentIds.size > 0 ? `${selectedComponentIds.size} ` : ''}Component{selectedComponentIds.size !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddComponentModal;

